import React, { ChangeEvent } from 'react';
import OriginalNumberFormat, { NumberFormatProps } from 'react-number-format';
import moment from 'moment';
import { DatePickerInput, TextField } from '@cherre-frontend/ui';
import {
  type CustomPropertyAttribute,
  CustomPropertyAttributeType,
} from '../../../../types/CustomPropertyAttribute';

const NumberFormat = OriginalNumberFormat as React.ComponentType<
  Omit<NumberFormatProps, 'size'>
>;

interface CustomAttributeFormFieldProps {
  value: string | number;
  attribute: CustomPropertyAttribute;
  onValueChange: (slug: string, value: string) => void;
}

const isDateCustomAttributeType = (type: string) => {
  return type === CustomPropertyAttributeType.DATE;
};

const isIntegerCustomAttributeType = (type: string) => {
  return type === CustomPropertyAttributeType.INTEGER;
};

const isNumericCustomAttribute = (type: string) => {
  return (
    isIntegerCustomAttributeType(type) ||
    type === CustomPropertyAttributeType.NUMBER
  );
};

const invalidDateMessage =
  'This field contains an invalid date value. This could be due to a change in the field type in the Configure Attributes menu.';

export const CustomAttributeFormField = ({
  value,
  attribute,
  onValueChange,
}: CustomAttributeFormFieldProps) => {
  const isDateAttribute = isDateCustomAttributeType(attribute.type);
  const isInvalidDateValue =
    isDateAttribute &&
    !!value &&
    (typeof value === 'number' || !moment(value).isValid());
  const sanitizedValue = isInvalidDateValue ? '' : value;

  const commonProps = {
    id: attribute.slug,
    'data-testid': attribute.slug,
    role: 'textbox',
    size: 'small' as const,
    variant: 'outlined' as const,
    value: sanitizedValue,
    required: attribute.required,
    placeholder: attribute.label,
  };

  if (isDateAttribute) {
    return (
      <DatePickerInput
        {...commonProps}
        testID={commonProps['data-testid']}
        slotProps={{ textField: { error: isInvalidDateValue } }}
        helperText={isInvalidDateValue ? invalidDateMessage : undefined}
        onChange={(value) => {
          onValueChange(
            attribute.slug,
            moment.isMoment(value) ? value.format('YYYY-MM-DD') : ''
          );
        }}
      />
    );
  }

  if (isNumericCustomAttribute(attribute.type)) {
    return (
      <NumberFormat
        {...commonProps}
        customInput={TextField}
        decimalScale={
          isIntegerCustomAttributeType(attribute.type) ? 0 : undefined
        }
        onValueChange={({ floatValue }) => {
          onValueChange(attribute.slug, floatValue ?? '');
        }}
      />
    );
  }

  return (
    <TextField
      {...commonProps}
      onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
        onValueChange(attribute.slug, target.value)
      }
    />
  );
};
